import React, { useState, useEffect, useCallback } from 'react';

const AdminPage = () => {
  console.log('AdminPage component rendering...');
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [token, setToken] = useState('');
  const [contentUrl, setContentUrl] = useState('');
  const [previewData, setPreviewData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [takedownHistory, setTakedownHistory] = useState([]);
  const [contactSubmissions, setContactSubmissions] = useState([]);
  const [reason, setReason] = useState('');

  useEffect(() => {
    const savedToken = localStorage.getItem('admin_token');
    if (savedToken) {
      setToken(savedToken);
      setIsLoggedIn(true);
      loadTakedownHistory(savedToken);
      loadContactSubmissions(savedToken);
    }
  }, []);

  const apiUrl = process.env.REACT_APP_API_URL || 'https://fileshare-backend.couriertrackerinfo.workers.dev';

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await fetch(`${apiUrl}/api/admin/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password })
      });
      const data = await response.json();
      if (data.success) {
        setToken(data.token);
        setIsLoggedIn(true);
        localStorage.setItem('admin_token', data.token);
        loadTakedownHistory(data.token);
        alert('✅ Admin login successful!');
      } else {
        alert(`❌ Login failed: ${data.error}`);
      }
    } catch (error) {
      alert(`❌ Login error: ${error.message}`);
    }
    setLoading(false);
  };

  const handlePreview = async () => {
    if (!contentUrl.trim()) {
      alert('Please enter a URL');
      return;
    }
    setLoading(true);
    try {
      const response = await fetch(`${apiUrl}/api/admin/preview`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ url: contentUrl })
      });
      const data = await response.json();
      if (data.success) {
        setPreviewData(data.content);
      } else {
        alert(`❌ Preview failed: ${data.error}`);
      }
    } catch (error) {
      alert(`❌ Preview error: ${error.message}`);
    }
    setLoading(false);
  };

  const handleTakedown = async () => {
    if (!previewData || !reason.trim()) {
      alert('Please preview content and enter a reason for takedown');
      return;
    }
    if (!window.confirm(`⚠️ Are you sure you want to delete this folder?\n\n"${previewData.folder.folder_name}"\n${previewData.folder.file_count} files\n\nThis action cannot be undone!`)) {
      return;
    }
    setLoading(true);
    try {
      const response = await fetch(`${apiUrl}/api/admin/takedown`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ url: contentUrl, reason: reason.trim() })
      });
      const data = await response.json();
      if (data.success) {
        alert(`✅ Content removed successfully!\n\nDeleted: ${data.deletedContent.name}\nFiles removed: ${data.deletedContent.deletedFiles}\nReason: ${data.reason}\n\nUsers will now see copyright notice when visiting this link.`);
        setPreviewData(null);
        setContentUrl('');
        setReason('');
        loadTakedownHistory(token);
      } else {
        alert(`❌ Takedown failed: ${data.error}`);
      }
    } catch (error) {
      alert(`❌ Takedown error: ${error.message}`);
    }
    setLoading(false);
  };

  const loadTakedownHistory = useCallback(async (authToken) => {
    try {
      const response = await fetch(`${apiUrl}/api/admin/history?limit=20`, {
        headers: { 'Authorization': `Bearer ${authToken}` }
      });
      const data = await response.json();
      if (data.success) {
        setTakedownHistory(data.takedowns);
      }
    } catch (error) {
      console.error('Failed to load takedown history:', error);
    }
  }, [apiUrl]);

  const loadContactSubmissions = useCallback(async (authToken) => {
    try {
      const response = await fetch(`${apiUrl}/api/admin/contact-submissions`, {
        headers: { 'Authorization': `Bearer ${authToken}` }
      });
      const data = await response.json();
      if (data.success) {
        setContactSubmissions(data.submissions);
      }
    } catch (error) {
      console.error('Failed to load contact submissions:', error);
    }
  }, [apiUrl]);

  const handleMarkResolved = async (submissionId, status) => {
    try {
      const response = await fetch(`${apiUrl}/api/admin/contact-submissions/${submissionId}`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${localStorage.getItem('admin_token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ status: status })
      });
      
      if (response.ok) {
        // Reload contact submissions
        loadContactSubmissions(localStorage.getItem('admin_token'));
        alert(`✅ Submission marked as ${status}`);
      } else {
        alert(`❌ Failed to update submission`);
      }
    } catch (error) {
      console.error('Failed to update submission:', error);
      alert(`❌ Error updating submission`);
    }
  };

  const handleLogout = () => {
    setIsLoggedIn(false);
    setToken('');
    setEmail('');
    setPassword('');
    setPreviewData(null);
    setContentUrl('');
    setTakedownHistory([]);
    localStorage.removeItem('admin_token');
  };

  const formatDate = (dateString) => new Date(dateString).toLocaleString();
  const formatFileSize = (bytes) => {
    if (!bytes) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  if (!isLoggedIn) {
    return (
      <div style={{ minHeight: '100vh', backgroundColor: '#1a1d29', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
        <div style={{ backgroundColor: '#2d3748', padding: '40px', borderRadius: '12px', width: '100%', maxWidth: '400px', boxShadow: '0 10px 25px rgba(0,0,0,0.3)' }}>
          <h1 style={{ color: '#e2e8f0', textAlign: 'center', marginBottom: '30px', fontSize: '24px', fontWeight: '600' }}>🛡️ Admin Panel</h1>
          <form onSubmit={handleLogin}>
            <div style={{ marginBottom: '20px' }}>
              <label style={{ color: '#a0aec0', fontSize: '14px', marginBottom: '8px', display: 'block' }}>Admin Email:</label>
              <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required
                style={{ width: '100%', padding: '12px', borderRadius: '8px', border: '1px solid #4a5568', backgroundColor: '#1a202c', color: '#e2e8f0', fontSize: '16px' }}
                placeholder="Enter admin email" />
            </div>
            <div style={{ marginBottom: '30px' }}>
              <label style={{ color: '#a0aec0', fontSize: '14px', marginBottom: '8px', display: 'block' }}>Password:</label>
              <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required
                style={{ width: '100%', padding: '12px', borderRadius: '8px', border: '1px solid #4a5568', backgroundColor: '#1a202c', color: '#e2e8f0', fontSize: '16px' }}
                placeholder="Enter password" />
            </div>
            <button type="submit" disabled={loading}
              style={{ width: '100%', padding: '12px', backgroundColor: loading ? '#4a5568' : '#3182ce', color: 'white', border: 'none', borderRadius: '8px', fontSize: '16px', fontWeight: '600', cursor: loading ? 'not-allowed' : 'pointer' }}>
              {loading ? 'Logging in...' : 'Login'}
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f8f9fa', padding: '12px', color: '#202124' }}>
      <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px', padding: '12px', backgroundColor: '#ffffff', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
          <h1 style={{ fontSize: '20px', fontWeight: '500', margin: 0, color: '#202124' }}>🛡️ Copyright Takedown System</h1>
          <button onClick={handleLogout} style={{ padding: '6px 12px', backgroundColor: '#ea4335', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '13px', fontWeight: '500' }}>Logout</button>
        </div>
        
        <div style={{ backgroundColor: '#ffffff', padding: '12px', borderRadius: '8px', marginBottom: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
          <h2 style={{ fontSize: '16px', marginBottom: '8px', color: '#202124', fontWeight: '500' }}>🚨 Content Takedown</h2>
          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', marginBottom: '6px', color: '#3c4043', fontSize: '13px', fontWeight: '500' }}>Folder Link:</label>
            <input type="url" value={contentUrl} onChange={(e) => setContentUrl(e.target.value)} placeholder="https://largetransfer.net/d/folderid"
              style={{ width: '100%', padding: '10px', borderRadius: '4px', border: '1px solid #dadce0', backgroundColor: '#ffffff', color: '#202124', fontSize: '13px' }} />
          </div>
          <button onClick={handlePreview} disabled={loading}
            style={{ padding: '10px 20px', backgroundColor: loading ? '#dadce0' : '#1a73e8', color: 'white', border: 'none', borderRadius: '4px', cursor: loading ? 'not-allowed' : 'pointer', fontSize: '13px', fontWeight: '500', marginBottom: '16px' }}>
            {loading ? 'Loading...' : '🔍 Preview Content'}
          </button>

          {previewData && (
            <div style={{ backgroundColor: '#f8f9fa', padding: '16px', borderRadius: '4px', marginBottom: '16px', border: '1px solid #dadce0' }}>
              <h3 style={{ marginBottom: '12px', color: '#202124', fontSize: '14px', fontWeight: '500' }}>📁 Content Preview</h3>
              <p style={{ marginBottom: '6px', color: '#3c4043', fontSize: '12px' }}><strong>Folder Name:</strong> {previewData.folder.folder_name || 'Unnamed'}</p>
              <p style={{ marginBottom: '6px', color: '#3c4043', fontSize: '12px' }}><strong>File Count:</strong> {previewData.folder.file_count}</p>
              <p style={{ marginBottom: '6px', color: '#3c4043', fontSize: '12px' }}><strong>Total Size:</strong> {formatFileSize(previewData.folder.total_size)}</p>
              <p style={{ marginBottom: '12px', color: '#3c4043', fontSize: '12px' }}><strong>Created:</strong> {formatDate(previewData.folder.created_at)}</p>
              
              <div style={{ marginTop: '12px' }}>
                <strong style={{ color: '#202124', fontSize: '12px' }}>Sample Files ({previewData.files.length} shown):</strong>
                <div style={{ maxHeight: '150px', overflowY: 'auto', marginTop: '8px' }}>
                  {previewData.files.map((file, index) => (
                    <div key={index} style={{ padding: '6px', backgroundColor: '#ffffff', marginBottom: '4px', borderRadius: '3px', fontSize: '11px', border: '1px solid #dadce0' }}>
                      📄 {file.original_filename} ({formatFileSize(file.file_size)})
                    </div>
                  ))}
                </div>
              </div>

              <div style={{ marginTop: '16px' }}>
                <label style={{ display: 'block', marginBottom: '6px', color: '#3c4043', fontSize: '12px', fontWeight: '500' }}>Reason for Takedown:</label>
                <input type="text" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Copyright violation, DMCA request, etc."
                  style={{ width: '100%', padding: '10px', borderRadius: '4px', border: '1px solid #dadce0', backgroundColor: '#ffffff', color: '#202124', marginBottom: '12px', fontSize: '12px' }} />
                <button onClick={handleTakedown} disabled={loading}
                  style={{ padding: '10px 20px', backgroundColor: loading ? '#dadce0' : '#ea4335', color: 'white', border: 'none', borderRadius: '4px', cursor: loading ? 'not-allowed' : 'pointer', fontWeight: '500', fontSize: '12px' }}>
                  {loading ? 'Removing...' : '🗑️ DELETE CONTENT'}
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Takedown History Table */}
        <div style={{ backgroundColor: '#ffffff', padding: '12px', borderRadius: '8px', marginBottom: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
          <h2 style={{ fontSize: '16px', marginBottom: '8px', color: '#202124', fontWeight: '500' }}>📋 Takedown History ({takedownHistory.length})</h2>
          {takedownHistory.length === 0 ? (
            <p style={{ color: '#5f6368', fontSize: '13px' }}>No takedowns recorded yet.</p>
          ) : (
            <div style={{ border: '1px solid #dadce0', borderRadius: '4px', overflow: 'hidden' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                <thead>
                  <tr style={{ backgroundColor: '#f8f9fa', borderBottom: '1px solid #dadce0' }}>
                    <th style={{ padding: '8px 12px', textAlign: 'left', fontWeight: '500', color: '#3c4043', borderRight: '1px solid #dadce0', width: '15%' }}>Content</th>
                    <th style={{ padding: '8px 12px', textAlign: 'left', fontWeight: '500', color: '#3c4043', borderRight: '1px solid #dadce0', width: '25%' }}>Reason</th>
                    <th style={{ padding: '8px 12px', textAlign: 'left', fontWeight: '500', color: '#3c4043', borderRight: '1px solid #dadce0', width: '20%' }}>Reporter</th>
                    <th style={{ padding: '8px 12px', textAlign: 'left', fontWeight: '500', color: '#3c4043', borderRight: '1px solid #dadce0', width: '20%' }}>URL</th>
                    <th style={{ padding: '8px 12px', textAlign: 'left', fontWeight: '500', color: '#3c4043', borderRight: '1px solid #dadce0', width: '12%' }}>Date</th>
                    <th style={{ padding: '8px 12px', textAlign: 'left', fontWeight: '500', color: '#3c4043', width: '8%' }}>Status</th>
                  </tr>
                </thead>
                <tbody>
              {takedownHistory.map((takedown, index) => (
                    <tr key={index} style={{ 
                      backgroundColor: index % 2 === 0 ? '#ffffff' : '#f8f9fa',
                      borderBottom: index < takedownHistory.length - 1 ? '1px solid #dadce0' : 'none'
                    }}>
                      <td style={{ padding: '8px 12px', borderRight: '1px solid #dadce0', fontWeight: '500', color: '#202124', fontSize: '11px' }}>
                        {takedown.content_type.toUpperCase()} {takedown.content_id}
                      </td>
                      <td style={{ padding: '8px 12px', borderRight: '1px solid #dadce0', color: '#3c4043', fontSize: '11px' }}>
                        <div style={{ wordBreak: 'break-word', lineHeight: '1.3' }}>
                          {takedown.reason.length > 50 ? `${takedown.reason.substring(0, 50)}...` : takedown.reason}
                        </div>
                      </td>
                      <td style={{ padding: '8px 12px', borderRight: '1px solid #dadce0', fontSize: '11px' }}>
                        {takedown.reporter_name ? (
                          <div>
                            <div style={{ color: '#1a73e8', fontWeight: '500' }}>{takedown.reporter_name}</div>
                            <div style={{ fontSize: '10px', color: '#5f6368' }}>{takedown.reporter_email}</div>
                          </div>
                        ) : (
                          <span style={{ color: '#5f6368' }}>System</span>
                        )}
                      </td>
                      <td style={{ padding: '8px 12px', borderRight: '1px solid #dadce0', fontSize: '10px' }}>
                        <a href={takedown.content_url} target="_blank" rel="noopener noreferrer" style={{ 
                          color: '#1a73e8', 
                          textDecoration: 'none', 
                          wordBreak: 'break-all',
                          lineHeight: '1.3'
                        }}>
                          {takedown.content_url}
                        </a>
                      </td>
                      <td style={{ padding: '8px 12px', borderRight: '1px solid #dadce0', color: '#5f6368', fontSize: '10px' }}>
                        {formatDate(takedown.created_at)}
                      </td>
                      <td style={{ padding: '8px 12px' }}>
                        {(() => {
                          // admin history rows from /api/admin/history don't include a status field
                          const rawStatus = takedown.status || 'deleted';
                          const normalized = String(rawStatus).toLowerCase();
                          const isPending = normalized === 'pending_review';
                          const isApproved = normalized === 'approved' || normalized === 'deleted';
                          const bg = isPending ? '#fef7e0' : isApproved ? '#e6f4ea' : '#fce8e6';
                          const color = isPending ? '#b06000' : isApproved ? '#137333' : '#d93025';
                          const border = isPending ? '1px solid #f9ab00' : isApproved ? '1px solid #34a853' : '1px solid #ea4335';
                          const label = normalized === 'deleted' ? 'DELETED' : String(rawStatus).replace('_', ' ').toUpperCase();
                          return (
                            <span style={{
                              padding: '2px 6px',
                              borderRadius: '3px',
                              fontSize: '9px',
                              fontWeight: '500',
                              backgroundColor: bg,
                              color,
                              border
                            }}>
                              {label}
                            </span>
                          );
                        })()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Contact Form Submissions Table */}
        <div style={{ backgroundColor: '#ffffff', padding: '12px', borderRadius: '8px', marginTop: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
          <h2 style={{ fontSize: '16px', marginBottom: '8px', color: '#202124', fontWeight: '500' }}>📧 Contact Form Submissions ({contactSubmissions.length})</h2>
          {contactSubmissions.length === 0 ? (
            <p style={{ color: '#5f6368', fontSize: '13px' }}>No contact form submissions yet.</p>
          ) : (
            <div style={{ border: '1px solid #dadce0', borderRadius: '4px', overflow: 'auto', maxHeight: '360px' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                <thead>
                  <tr style={{ backgroundColor: '#f8f9fa', borderBottom: '1px solid #dadce0' }}>
                    <th style={{ padding: '6px 10px', textAlign: 'left', fontWeight: '500', color: '#3c4043', borderRight: '1px solid #dadce0', width: '14%' }}>Subject</th>
                    <th style={{ padding: '6px 10px', textAlign: 'left', fontWeight: '500', color: '#3c4043', borderRight: '1px solid #dadce0', width: '12%' }}>Name</th>
                    <th style={{ padding: '6px 10px', textAlign: 'left', fontWeight: '500', color: '#3c4043', borderRight: '1px solid #dadce0', width: '16%' }}>Email</th>
                    <th style={{ padding: '6px 10px', textAlign: 'left', fontWeight: '500', color: '#3c4043', borderRight: '1px solid #dadce0', width: '25%' }}>Message</th>
                    <th style={{ padding: '6px 10px', textAlign: 'left', fontWeight: '500', color: '#3c4043', borderRight: '1px solid #dadce0', width: '19%' }}>Reported URL</th>
                    <th style={{ padding: '6px 10px', textAlign: 'left', fontWeight: '500', color: '#3c4043', borderRight: '1px solid #dadce0', width: '10%' }}>Date</th>
                    <th style={{ padding: '6px 10px', textAlign: 'left', fontWeight: '500', color: '#3c4043', width: '4%' }}>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {contactSubmissions.map((submission, index) => (
                    <tr key={index} style={{ 
                      backgroundColor: index % 2 === 0 ? '#ffffff' : '#f8f9fa',
                      borderBottom: index < contactSubmissions.length - 1 ? '1px solid #dadce0' : 'none'
                    }}>
                      <td style={{ padding: '6px 10px', borderRight: '1px solid #dadce0', fontWeight: '500', color: '#1a73e8', fontSize: '11px' }}>
                        {submission.subject}
                      </td>
                      <td style={{ padding: '6px 10px', borderRight: '1px solid #dadce0', fontSize: '11px', color: '#202124', fontWeight: '500' }}>
                        {submission.name}
                      </td>
                      <td style={{ padding: '6px 10px', borderRight: '1px solid #dadce0', fontSize: '10px', color: '#5f6368' }}>
                        {submission.email}
                      </td>
                      <td style={{ padding: '6px 10px', borderRight: '1px solid #dadce0', color: '#3c4043', fontSize: '11px' }}>
                        <div style={{ wordBreak: 'break-word', lineHeight: '1.3' }}>
                          {submission.message.length > 60 ? `${submission.message.substring(0, 60)}...` : submission.message}
                  </div>
                      </td>
                      <td style={{ padding: '6px 10px', borderRight: '1px solid #dadce0', fontSize: '10px' }}>
                        {submission.folder_url ? (
                          <a href={submission.folder_url} target="_blank" rel="noopener noreferrer" style={{ 
                            color: '#1a73e8', 
                            textDecoration: 'none', 
                            wordBreak: 'break-all',
                            lineHeight: '1.3'
                          }}>
                            {submission.folder_url}
                          </a>
                        ) : (
                          <span style={{ color: '#5f6368' }}>-</span>
                        )}
                      </td>
                      <td style={{ padding: '6px 10px', borderRight: '1px solid #dadce0', color: '#5f6368', fontSize: '10px' }}>
                        {formatDate(submission.timestamp)}
                      </td>
                      <td style={{ padding: '6px 10px' }}>
                        <div style={{ display: 'flex', gap: '4px', flexDirection: 'column' }}>
                          {submission.status === 'new' ? (
                            <>
                              <button 
                                onClick={() => handleMarkResolved(submission.submission_id, 'resolved')}
                                style={{
                                  padding: '2px 6px',
                                  borderRadius: '3px',
                                  fontSize: '9px',
                                  fontWeight: '500',
                                  backgroundColor: '#e6f4ea',
                                  color: '#137333',
                                  border: '1px solid #34a853',
                                  cursor: 'pointer'
                                }}
                              >
                                ✓ RESOLVED
                              </button>
                              <button 
                                onClick={() => handleMarkResolved(submission.submission_id, 'dismissed')}
                                style={{
                                  padding: '2px 6px',
                                  borderRadius: '3px',
                                  fontSize: '9px',
                                  fontWeight: '500',
                                  backgroundColor: '#fce8e6',
                                  color: '#d93025',
                                  border: '1px solid #ea4335',
                                  cursor: 'pointer'
                                }}
                              >
                                ✗ DISMISS
                              </button>
                            </>
                          ) : (
                            <span style={{
                              padding: '2px 6px',
                              borderRadius: '3px',
                              fontSize: '9px',
                              fontWeight: '500',
                              backgroundColor: submission.status === 'resolved' ? '#e6f4ea' : '#f3f4f6',
                              color: submission.status === 'resolved' ? '#137333' : '#5f6368',
                              border: submission.status === 'resolved' ? '1px solid #34a853' : '1px solid #dadce0'
                            }}>
                              {submission.status.toUpperCase()}
                            </span>
                          )}
                </div>
                      </td>
                    </tr>
              ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminPage; 